import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

// Function to parse markdown links and bold text, and render them appropriately
const renderMessageWithLinks = (content) => {
  // First, handle bold text: **text**
  let processedContent = content;
  const boldParts = [];
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let lastBoldIndex = 0;
  let boldMatch;
  
  while ((boldMatch = boldRegex.exec(content)) !== null) {
    if (boldMatch.index > lastBoldIndex) {
      boldParts.push({ type: 'text', content: content.slice(lastBoldIndex, boldMatch.index) });
    }
    boldParts.push({ type: 'bold', content: boldMatch[1] });
    lastBoldIndex = boldMatch.index + boldMatch[0].length;
  }
  if (lastBoldIndex < content.length) {
    boldParts.push({ type: 'text', content: content.slice(lastBoldIndex) });
  }
  
  // Now process each part for links
  const finalParts = [];
  boldParts.forEach((part, partIdx) => {
    if (part.type === 'bold') {
      finalParts.push(<strong key={`bold-${partIdx}`}>{part.content}</strong>);
    } else {
      // Process links in text parts
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let lastIndex = 0;
      let match;
      const textContent = part.content;
      
      while ((match = linkRegex.exec(textContent)) !== null) {
        if (match.index > lastIndex) {
          finalParts.push(textContent.slice(lastIndex, match.index));
        }
        
        const linkText = match[1];
        const linkUrl = match[2];
        
        if (linkUrl.startsWith('/')) {
          finalParts.push(
            <Link 
              key={`link-${partIdx}-${match.index}`} 
              to={linkUrl}
              className="chat-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}
            </Link>
          );
        } else {
          finalParts.push(
            <a 
              key={`link-${partIdx}-${match.index}`} 
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-link"
            >
              {linkText}
            </a>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      if (lastIndex < textContent.length) {
        finalParts.push(textContent.slice(lastIndex));
      }
    }
  });
  
  return finalParts.length > 0 ? finalParts : content;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tezza icon URL - custom filmmaker character
const TEZZA_ICON = "https://customer-assets.emergentagent.com/job_bcb7959a-5c11-4a02-a3be-bbbaf5b483ed/artifacts/olyt3kar_ai-agent.png";

const SupportChat = () => {
  const location = useLocation();
  const isStreamPage = location.pathname === '/stream';
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "G'day! I'm Tezza, your AI filmmaker assistant. Having trouble with your stream? Whether it's payment hiccups, magic link mysteries, or video playback dramas - I've got you covered. What can I help with?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleCount, setBubbleCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatWindowRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle iOS keyboard viewport resize
  useEffect(() => {
    if (!isOpen) return;
    
    const handleResize = () => {
      // On iOS, when keyboard opens, visualViewport height changes
      if (window.visualViewport && chatWindowRef.current) {
        const viewportHeight = window.visualViewport.height;
        chatWindowRef.current.style.height = `${viewportHeight}px`;
      }
    };

    // Listen to visualViewport resize (fires when iOS keyboard opens/closes)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
      handleResize(); // Initial call
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, [isOpen]);

  // Auto-scroll to input when user focuses on typing
  const handleInputFocus = () => {
    // Small delay to let keyboard open on mobile
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  // Notification bubble animation - shows 2 times every 30 seconds
  useEffect(() => {
    if (isOpen || bubbleCount >= 2) return;

    const showNotification = () => {
      if (bubbleCount >= 2 || isOpen) return;
      
      setShowBubble(true);
      setBubbleCount(prev => prev + 1);
      
      // Hide bubble after 4 seconds
      setTimeout(() => {
        setShowBubble(false);
      }, 4000);
    };

    // Show first bubble after 5 seconds
    const initialDelay = bubbleCount === 0 ? 5000 : 30000;
    const timer = setTimeout(showNotification, initialDelay);

    return () => clearTimeout(timer);
  }, [bubbleCount, isOpen]);

  // Stop showing bubble when chat is opened
  const handleOpenChat = () => {
    setIsOpen(true);
    setShowBubble(false);
    setBubbleCount(2); // Stop future notifications
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. Please email support@washedaway.au for assistance."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    { label: 'I Paid But...', message: "I paid and it's asking me to pay again" },
    { label: 'Magic Link Help', message: "I didn't receive my magic link email" },
    { label: 'Video Not Playing', message: "The video won't play" },
  ];

  return (
    <>
      {/* Notification Bubble */}
      {showBubble && !isOpen && (
        <div className={`tezza-notification-bubble ${isStreamPage ? 'bottom-position' : ''}`} onClick={handleOpenChat}>
          <span>If you need help, just ask me!</span>
          <div className="tezza-bubble-arrow"></div>
        </div>
      )}

      {/* Chat Button - Tezza */}
      <button
        onClick={handleOpenChat}
        className={`support-chat-button ${isOpen ? 'hidden' : ''} ${isStreamPage ? 'bottom-position' : ''}`}
        data-testid="support-chat-button"
        aria-label="Chat with Tezza"
      >
        <img src={TEZZA_ICON} alt="Tezza" className="tezza-btn-icon" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatWindowRef}
          className={`support-chat-window ${isStreamPage ? 'bottom-position' : ''}`} 
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div className="support-chat-header">
            <div className="support-chat-header-info">
              <div className="support-chat-avatar">
                <img src={TEZZA_ICON} alt="Tezza" className="tezza-avatar" />
              </div>
              <div>
                <h3 className="support-chat-title">Tezza</h3>
                <p className="support-chat-status">AI Filmmaker Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="support-chat-close"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="support-chat-messages">
            {messages.map((msg, idx) => (
              <div
                key={`msg-${idx}-${msg.role}`}
                className={`support-chat-message ${msg.role === 'user' ? 'user' : 'assistant'}`}
              >
                {msg.role === 'assistant' ? renderMessageWithLinks(msg.content) : msg.content}
              </div>
            ))}
            {isLoading && (
              <div className="support-chat-message assistant">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (show only if few messages) */}
          {messages.length <= 2 && (
            <div className="support-chat-quick-actions">
              {quickActions.map((action) => (
                <button
                  key={`quick-${action.label}`}
                  onClick={() => {
                    setInput(action.message);
                  }}
                  className="support-chat-quick-btn"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="support-chat-input-form">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={handleInputFocus}
              placeholder="Type your question..."
              className="support-chat-input"
              disabled={isLoading}
              data-testid="support-chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="support-chat-send"
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportChat;
